import React, { useContext } from 'react'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import { WithdrawalAutomationGlobalSettingsPayload } from '@/api/Wallet/Types/WithdrawalAutomationDto'
import { Loading } from '@/components/Loading'
import { errorToast, successToast, ToastContext } from '@/components/Toast/Toast'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { useApiClient } from '@/utils/api/ApiClient'
import { useCallbackWithForceRefresh } from '@/utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from '@/utils/api/useFetch'
import { WalletsApiClient } from '@/utils/api/walletsApiClient'
import { AuthSessionContext } from '@/utils/AuthContext'
import { links } from '@/utils/featureToggles'
import { hasPermission } from '@/utils/permissionsValidator'

import { GlobalControlsForm } from './GlobalControlsForm'
import {
  MLWithdrawalAutomationNavigation,
  MLWithdrawalAutomationTab,
} from './MLWithdrawalAutomationNavigation'
import { useWithdrawalAutomationOptions } from './useWithdrawalAutomationOptions'

export const MLWithdrawalAutomationGlobalControlsPage: React.FC = () => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []
  const canEdit = hasPermission(permissions, PermissionsEnum.CanEditMLWithdrawalAutomationSettings)

  const { currencyOptions } = useWithdrawalAutomationOptions()

  const { callback, forceRefresh } = useCallbackWithForceRefresh(
    () => walletsApiClient.getWithdrawalAutomationGlobalSettings(),
    []
  )
  const { data, isLoading } = useFetchOne(callback)

  const handleSubmit = async (values: WithdrawalAutomationGlobalSettingsPayload) => {
    try {
      await walletsApiClient.updateWithdrawalAutomationGlobalSettings(values)
      setToast(successToast('Global settings updated successfully.'))
      forceRefresh()
    } catch (error) {
      setToast(errorToast((error as Error).message))
    }
  }

  const handleToggleMaster = async (isMasterEnabled: boolean) => {
    try {
      await walletsApiClient.setWithdrawalAutomationMasterEnabled({ isMasterEnabled })
      setToast(
        successToast(`ML auto-processing ${isMasterEnabled ? 'enabled' : 'disabled'} successfully.`)
      )
      forceRefresh()
    } catch (error) {
      setToast(errorToast((error as Error).message))
    }
  }

  if (!hasPermission(permissions, PermissionsEnum.CanViewMLWithdrawalAutomationSettings)) {
    return null
  }

  return (
    <>
      <Breadcrumbs
        crumbs={[
          {
            title: 'Withdrawal Automation (ML)',
            link: links.administrationMlWithdrawalAutomationGlobalControls.url,
          },
        ]}
      />
      <MLWithdrawalAutomationNavigation tab={MLWithdrawalAutomationTab.GLOBAL_CONTROLS} />
      {isLoading || !data ? (
        <Loading isLoading />
      ) : (
        <GlobalControlsForm
          initialValues={data}
          canEdit={canEdit}
          currencyOptions={currencyOptions}
          onSubmit={handleSubmit}
          onToggleMaster={handleToggleMaster}
        />
      )}
    </>
  )
}
